import React, { Component } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import {
  formatCurrency,
  TableGrid,
  export2Excel,
  export2PDF,
} from '@C/TableGrid';
import services from '@C/StaffManagement/_utils/data';
import { PaymentMethodInfo, StaffList } from '@C/Utils';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';

const col = [
  {
    name: 'Date',
    key: 'time_stamp',
    sortable: true,
    type: 'date',
  },
  {
    name: 'Employee',
    key: 'employee',
    formatter(props) {
      return (
        <>
          {props.first_name} {props.last_name}
        </>
      );
    },
  },
  {
    name: 'Salary type',
    key: 'salary_type',
    formatter(props) {
      return <>{props.salary_mode}</>;
    },
  },
  {
    name: 'Salary amount',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.salary)}</>;
    },
  },
  {
    name: 'Advance taken',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.advance_taken)}</>;
    },
  },
  {
    name: 'Advance adjusted',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.settled_amount)}</>;
    },
  },
  {
    name: 'Incentives',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.other_incentives)}</>;
    },
  },
  {
    name: 'Bonus',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.bonus)}</>;
    },
  },
  {
    name: 'Deductions',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.deduction)}</>;
    },
  },
  {
    name: 'Salary paid',
    key: 'all',
    formatter(props) {
      return <>{formatCurrency(props.paid_amount)}</>;
    },
  },
  {
    name: 'Paid by',
    key: 'salary_given',
    formatter(props) {
      return (
        <>
          {props.first_name} {props.last_name}
        </>
      );
    },
  },
  {
    name: 'Payment mode',
    key: 'all',
    formatter(props) {
      return (
        <>
          <PaymentMethodInfo
            source={props.payment.source}
            method={props.payment.payment_method}
            bank={props.bank}
          />
        </>
      );
    },
  },
  {
    name: 'Notes',
    key: 'notes',
  },
];

const enabledActions = [
  {
    action: 'Delete',
    roles: ['Manager', 'Dealer'],
  },
];

export default class AdvanceList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      salaryList: [],
      showModal: false,
      selectedData: '',
      start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      month: dayjs().format('MMMM').toUpperCase(),
      year: dayjs().format('YYYY'),
      staff_id: '',
      given_by: '',
      offset: 1,
      count: 0,
      limit: 20,
      loading: true,
    };

    this.limitInput = React.createRef();
  }

  componentDidMount() {
    this.getSalaryList();
  }

  getSalaryList = () => {
    services
      .getSalaryList(this.state)
      .then((res) => {
        this.setState({
          salaryList: res.data.data.results,
          count: res.data.data.count,
          loading: false,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  handlePagination = (e) => {
    this.setState({ offset: e, loading: true }, function () {
      this.getSalaryList();
    });
  };

  handleDateChange = (e) => {
    const { name, value } = e.target;
    if (name === 'start_date' && dayjs(value).isAfter(this.state.end_date)) {
      this.setState({ [name]: value, end_date: value });
    } else if (
      name === 'end_date' &&
      dayjs(value).isBefore(this.state.start_date)
    ) {
      this.setState({ [name]: value, start_date: value });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleStaffChange = (e) => {
    this.setState({ staff_id: e.value });
  };

  handleProviderChange = (e) => {
    this.setState({ given_by: e.value });
  };

  handleQuickSearch = (e) => {
    if (e.target.value === '' || e.target.value === '0') {
      return;
    }
    this.setState({ limit: e.target.value, offset: 1 }, function () {
      this.getSalaryList();
    });
  };

  resetFilter = () => {
    this.setState(
      {
        start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
        month: dayjs().format('MMMM').toUpperCase(),
        year: dayjs().format('YYYY'),
        staff_id: '',
        given_by: '',
        offset: 1,
        count: 0,
        limit: 20,
        loading: true,
      },
      function () {
        this.getSalaryList();
      },
    );
  };

  confirmDelete = (item) => {
    confirmAlert({
      title: 'Confirm transaction deletion',
      message: 'Are you sure',
      buttons: [
        {
          label: 'Yes',
          onClick: () => this.deleteItem(item),
        },
        {
          label: 'No',
          onClick: () => false,
        },
      ],
    });
  };

  deleteItem = (item) => {
    const headers = authHeader(1);
    axios
      .delete(
        `${process.env.API_URL}/staff-management/bunk/salary/?uuid=${item.uuid}`,
        {
          headers,
        },
      )
      .then(() => {
        toast.success('Transaction deleted successfully');
        this.getSalaryList();
      });
  };

  submitForm = (e) => {
    e.preventDefault();
    this.setState({ loading: true }, function () {
      this.getSalaryList();
    });
  };

  render() {
    const { salaryList } = this.state;
    const exportOptions = {
                        fileName: `Salary Report  - ${this.state.start_date} to ${this.state.end_date}`,
                        reportName: 'Salary Report',
                        reportDate: `Date - ${dayjs(this.state.start_date).format(
                          'D MMM YY',
                        )} to ${dayjs(this.state.end_date).format('D MMM YY')}`,
                        reportQuantity: '',
                        reportSales: '',
                        reportTesting: '',
                        reportBowser: '',
                        reportTotal: '',
                        reportCustomerName: '',
                        reportGst: '',
                        reportOpeningBalance: '',
                        reportClosingBalance: '',
                        reportSaleTotal: '',
                        reportTCSTotal: '',
                      };
    return (
      <>
        <section className="widget full-width list-table">
          <header>
            <div className="stock-info-headers">
              <h3> Salary List ({this.state.count})</h3>
              <div className="mini-summary">
                <ul>
                  <li>
                    <span>Total salary amount: </span>
                    <span>
                      {formatCurrency(
                        salaryList.reduce(
                          (a, b) => a + (b['paid_amount'] || 0),
                          0,
                        ) || 0,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="action">
              <button
                className="submit"
                type="submit"
                onClick={this.submitForm}
              >
                Filter
              </button>
              <button
                onClick={this.resetFilter}
                className="reset-filter"
              ></button>
            </div>
            <div className="export-btns">
              <button
                onClick={() => export2Excel(exportOptions)}
                className={`blue-btn btn-with-icon btn-with-icon__excel action-btn ${this.state.exportingExcel ? 'loading' : ''}`}
                title="Export to Excel"
              >
                Export to Excel
              </button>
              <button
                onClick={() => export2PDF(exportOptions)}
                className={`blue-btn btn-with-icon btn-with-icon__pdf action-btn ${this.state.exportingPdf ? 'loading' : ''}`}
                title="Export to PDF"
              >
                Export to PDF
              </button>
            </div>
          </header>
          <main>
            <div className="table-filters">
              <div className="filter-wrapper">
                <div className="filter-group">
                  <label>From date:</label>
                  <input
                    className="cstm-date-input"
                    type="date"
                    value={this.state.start_date}
                    name="start_date"
                    onChange={this.handleDateChange}
                  />
                </div>
                <div className="filter-group">
                  <label>To date:</label>
                  <input
                    className="cstm-date-input"
                    type="date"
                    value={this.state.end_date}
                    onChange={this.handleDateChange}
                    name="end_date"
                  />
                </div>
                <div className="filter-group">
                  <label>Staff:</label>
                  <StaffList
                    value={this.state.staff_id}
                    onChange={this.handleStaffChange}
                  />
                </div>
                <div className="filter-group">
                  <label>Given by:</label>
                  <StaffList
                    value={this.state.given_by}
                    onChange={this.handleProviderChange}
                  />
                </div>
              </div>
            </div>
            <div className="table-wrapper">
              {(() => {
                if (this.state.loading) {
                  return <DropLoaderLarge />;
                } else {
                  return (
                    <TableGrid
                      col={col}
                      row={salaryList}
                      limit={this.state.limit}
                      offset={this.state.offset}
                      count={this.state.count}
                      onEdit={this.props.onEdit}
                      onDelete={this.confirmDelete}
                      handlePagination={this.handlePagination}
                      enabledActions={enabledActions}
                      enableExport={false}
                      recordsFilter={this.handleQuickSearch}
                    />
                  );
                }
              })()}
            </div>
          </main>
        </section>
      </>
    );
  }
}
